import {
  getCountByStatus,
  useGetAllMultiplePrints,
} from "@/api/multipleprint/multipleprint";
import NewItemDialogTrigger from "@/components/modal/NewItemDialogTrigger";
import NewMultiprintDialog from "@/components/multiprint/NewMultiprintDialog";
import StatusTableTab from "@/components/StatusTableTab";
import { AdvancedSearch } from "@/components/table/AdvancedSearch";
import { AdvancedSearchChip } from "@/components/table/AdvancedSearchChip";
import { AdvancedSort } from "@/components/table/AdvancedSort";
import { GlobalSearch } from "@/components/table/GlobalSearch";
import { DataTable } from "@/components/ui/datatable";
import { ColumnFormatted, generateTable } from "@/config/dashboard/general";
import {
  multipleprintsAdvancedSearchConfig,
  multipleprintsColumns,
} from "@/config/dashboard/multipleprints";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { Multipleprint } from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const STORAGE_KEY = "multiprint-table-preferences";

const flatVisibleMultipleprintColumns = multipleprintsColumns.flatMap(
  (column: ColumnFormatted) => (column.visible ? column.key : []),
);

const MultiprintDashboard = () => {
  const {
    pagination,
    setPagination,
    countByStatus,
    currentCount,
    fetchParams,
    filterStatus,
    setFilterStatus,
  } = useDashboardParams<Multipleprint>({
    resourceName: "multipleprint",
    resourceCounter: getCountByStatus,
    statusField: "estampesMultiplesPublicationStatus",
  });

  const { t } = useTranslation();

  const navigate = useNavigate();

  const { data } = useGetAllMultiplePrints(fetchParams);

  const columns: ColumnDef<Multipleprint, any>[] = useMemo(() => {
    return generateTable(multipleprintsColumns, "multipleprint");
  }, []);

  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const { order } = JSON.parse(saved);
      return order || flatVisibleMultipleprintColumns;
    }
    return flatVisibleMultipleprintColumns;
  });

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { visibility } = JSON.parse(saved);
        return (
          visibility || {
            ...multipleprintsColumns.reduce(
              (acc, column) => ({
                ...acc,
                [column.key]: column.visible || false,
              }),
              {},
            ),
          }
        );
      }
      return {
        ...multipleprintsColumns.reduce(
          (acc, column) => ({
            ...acc,
            [column.key]: column.visible || false,
          }),
          {},
        ),
      };
    },
  );

  return (
    <div className="flex flex-col items-start gap-3">
      <div className="flex justify-between  items-center w-full">
        <p className="text-[32px] font-[600]">{t("entryMultipleprints")}</p>
        <GlobalSearch
          config={multipleprintsAdvancedSearchConfig}
          data={data ?? []}
        />
      </div>
      <div className="flex flex-row justify-between w-full">
        <StatusTableTab
          filterStatus={filterStatus}
          countByStatus={countByStatus}
          onValueChange={setFilterStatus}
        />
        <div className="flex flex-row gap-2">
          <AdvancedSearch
            config={multipleprintsAdvancedSearchConfig}
            resource="multipleprints"
          />
          <AdvancedSort config={multipleprintsAdvancedSearchConfig} />
          <NewItemDialogTrigger
            title={t("createNewmultipleprint")}
            Dialog={NewMultiprintDialog}
          />
        </div>
      </div>
      <div className="w-full flex flex-row">
        <AdvancedSearchChip />
      </div>
      <div className="w-full">
        <DataTable
          onRowClick={(row) =>
            navigate(`/edit/multipleprint/${row.original.id}`)
          }
          pagination={{
            pagination: pagination,
            setPage: setPagination,
            totalSize: currentCount,
          }}
          storageKey={STORAGE_KEY}
          columns={columns}
          data={data ?? []}
          options={{
            enableColumnResizing: true,
            state: {
              columnVisibility: columnVisibility,
              columnOrder: columnsOrder,
            },
            onColumnVisibilityChange: setColumnVisibility,
            onColumnOrderChange: setColumnsOrder,
          }}
        />
      </div>
    </div>
  );
};

export default MultiprintDashboard;
